// src/app/Analytics.js - Analytics Component
import React, { useState, useEffect } from 'react';
import {
  BarChart,
  TrendingUp,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  FileSpreadsheet,
} from 'lucide-react';
import { useData } from '../context/DataContext';

const Analytics = ({ setCurrentPage }) => {
  const {
    // eslint-disable-next-line
    clientData,
    getDashboardStats,
    fetchSheetData,
    // eslint-disable-next-line
    loading,
    sheetConfig,
    lastUpdated,
  } = useData();

  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (sheetConfig) {
        await fetchSheetData();
        setStats(await getDashboardStats());
      }
    };
    loadData();
  }, [sheetConfig, fetchSheetData, getDashboardStats]);

  const getCompletionRate = () => {
    if (!stats || stats.totalClients === 0) return 0;
    return Math.round((stats.completed / stats.totalClients) * 100);
  };
/*
  const getStatusTrend = () => {
    // Mock trend data - in real app this would be calculated from historical data
    return [
      { name: 'Week 1', completed: 12, inProgress: 8, pending: 5 },
      { name: 'Week 2', completed: 18, inProgress: 10, pending: 7 },
      { name: 'Week 3', completed: 22, inProgress: 12, pending: 6 },
      { name: 'Week 4', completed: 28, inProgress: 15, pending: 8 },
    ];
  };*/

  // Show setup prompt if no sheet is configured
  if (!sheetConfig) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Analytics</h1>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <BarChart className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No Data Available
            </h2>
            <p className="text-gray-600 mb-6">
              Connect your Google Sheet to view analytics and insights.
            </p>
            <button
              onClick={() => setCurrentPage('sheets-setup')}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              Setup Google Sheets
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <div className="flex items-center space-x-4">
            {/* Removed timeRange selector for now - will add back when implemented */}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Total Clients
                </h3>
                <p className="text-3xl font-bold text-blue-600">
                  {stats?.totalClients || 0}
                </p>
                <p className="text-sm text-gray-500">Active projects</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Completion Rate
                </h3>
                <p className="text-3xl font-bold text-green-600">
                  {getCompletionRate()}%
                </p>
                <p className="text-sm text-gray-500">Projects completed</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  In Progress
                </h3>
                <p className="text-3xl font-bold text-orange-600">
                  {stats?.inProgress || 0}
                </p>
                <p className="text-sm text-gray-500">Active work</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Avg. Time
                </h3>
                <p className="text-3xl font-bold text-yellow-600">12d</p>
                <p className="text-sm text-gray-500">To completion</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Status Breakdown */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Status Breakdown
            </h2>
            {stats?.statusBreakdown ? (
              <div className="space-y-3">
                {Object.entries(stats.statusBreakdown).map(
                  ([status, count]) => {
                    const percentage = Math.round(
                      (count / stats.totalClients) * 100
                    );
                    return (
                      <div
                        key={status}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <div className="w-3 h-3 rounded-full bg-blue-500 mr-3"></div>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {status}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">{count}</span>
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-500 w-8">
                            {percentage}%
                          </span>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No data available
              </p>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Project Insights
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-green-900">
                    High Completion Rate
                  </div>
                  <div className="text-xs text-green-700">
                    {getCompletionRate()}% of projects completed successfully
                  </div>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-blue-900">
                    Active Projects
                  </div>
                  <div className="text-xs text-blue-700">
                    {stats?.inProgress || 0} projects currently in progress
                  </div>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>

              {stats?.pending > 0 && (
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-yellow-900">
                      Pending Items
                    </div>
                    <div className="text-xs text-yellow-700">
                      {stats.pending} projects waiting to start
                    </div>
                  </div>
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Data Source Info */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Data Source
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-900">
                Google Sheets Integration
              </div>
              <div className="text-sm text-gray-500">
                {stats?.totalClients || 0} records • Last updated:{' '}
                {lastUpdated ? lastUpdated.toLocaleString() : 'Never'}
              </div>
            </div>
            <button
              onClick={() => setCurrentPage('sheets-setup')}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Manage Connection
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 flex justify-center space-x-4">
          <button
            onClick={() => setCurrentPage('dashboard')}
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back to Dashboard
          </button>

          <button
            onClick={() => setCurrentPage('portals')}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Manage Clients
          </button>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
